/**
 * =============================================================================
 * Página: ReferralMaterials
 * Versão: 1.3.0
 *
 * Descrição:
 *   Gestão administrativa dos materiais de divulgação do tenant.
 *   v1.1.0: modo "embedded" para reaproveitamento dentro do detalhe do
 *   programa — esconde header, trava filtro de programa e pré-seleciona
 *   no modal de criação.
 *   v1.2.0: filtros padronizados no estilo de Modelos de Contrato
 *   (busca + selects horizontais compactos, sem Card).
 *   v1.3.0: cards mais compactos (grid mais densa) + preview interno da
 *   mídia ao clicar na miniatura, reaproveitando o ImageLightbox padrão
 *   do sistema (sem abrir link externo).
 * =============================================================================
 */

import { useState, useMemo } from 'react';
import {
  ImageIcon,
  Plus,
  Pencil,
  Trash2,
  FileText,
  Video,
  File as FileIcon,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  useReferralMaterials,
  useReferralMaterialMutations,
  type AffiliateMaterial,
} from '@/hooks/useReferralMaterials';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { ReferralMaterialFormModal } from '@/components/referral/ReferralMaterialFormModal';
// Preview universal de mídias (imagem/vídeo/documento) — padrão do projeto
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
// v1.4.0 — descrição do material passa a aceitar HTML (editor rico)
import { sanitizeHtml, stripHtml } from '@/lib/sanitize';


const TYPE_LABEL: Record<string, string> = {
  image: 'Imagem',
  banner: 'Banner',
  video: 'Vídeo',
  document: 'Documento',
  copy: 'Texto',
};

function isImage(m: AffiliateMaterial) {
  if (m.mime_type?.startsWith('image/')) return true;
  return m.material_type === 'image' || m.material_type === 'banner';
}
function isVideo(m: AffiliateMaterial) {
  if (m.mime_type?.startsWith('video/')) return true;
  return m.material_type === 'video';
}

function PreviewIcon({ m }: { m: AffiliateMaterial }) {
  if (isImage(m)) return <ImageIcon className="h-4 w-4" />;
  if (isVideo(m)) return <Video className="h-4 w-4" />;
  if (m.material_type === 'copy') return <FileText className="h-4 w-4" />;
  return <FileIcon className="h-4 w-4" />;
}

// v1.1.0: props para modo embedded (drill-down dentro de um programa)
interface ReferralMaterialsProps {
  embeddedProgramId?: string;
  embedded?: boolean;
}

export default function ReferralMaterials({
  embeddedProgramId,
  embedded = false,
}: ReferralMaterialsProps = {}) {
  const { toast } = useToast();
  // Em embedded, fixa o programa do contexto.
  const [programId, setProgramId] = useState<string | 'all'>(
    embeddedProgramId ?? 'all',
  );
  const [type, setType] = useState<string | 'all'>('all');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  // v1.3.0 — mídia atualmente aberta no lightbox (preview interno)
  const [previewMedia, setPreviewMedia] = useState<MediaItem | null>(null);

  const [selected, setSelected] = useState<AffiliateMaterial | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AffiliateMaterial | null>(
    null,
  );

  const { data: programs = [] } = useReferralPrograms();
  const effectiveProgramId = embedded ? embeddedProgramId ?? 'all' : programId;
  const {
    data: materials = [],
    isLoading,
    isError,
  } = useReferralMaterials({ programId: effectiveProgramId, type });
  const { deleteMutation, updateMutation } = useReferralMaterialMutations();

  // Filtragem client-side por busca textual (título / descrição)
  const filteredMaterials = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return materials;
    return materials.filter((m) => {
      return (
        m.title?.toLowerCase().includes(term) ||
        // v1.4.0 — descrição pode conter HTML: busca no texto puro
        stripHtml(m.description).toLowerCase().includes(term)
      );
    });
  }, [materials, search]);

  const handleNew = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleEdit = (m: AffiliateMaterial) => {
    setSelected(m);
    setModalOpen(true);
  };

  const handleToggleActive = async (m: AffiliateMaterial) => {
    try {
      await updateMutation.mutateAsync({
        id: m.id,
        patch: { is_active: !m.is_active },
      });
    } catch (err: any) {
      toast({
        title: 'Erro ao atualizar',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteMutation.mutateAsync(confirmDelete);
      toast({ title: 'Material excluído.' });
      setConfirmDelete(null);
    } catch (err: any) {
      toast({
        title: 'Erro ao excluir',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Header — oculto em modo embedded */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <ImageIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate">Materiais de divulgação</h1>
              <p className="text-xs text-muted-foreground truncate">
                Banners, vídeos, copies e documentos disponíveis aos afiliados.
              </p>
            </div>
          </div>
          {/* v1.x — Padronizado com o botão "Novo Afiliado" (size xs, ícone 3.5). */}
          <Button onClick={handleNew} size="xs" className="shrink-0">
            <Plus className="h-3.5 w-3.5" />
            Novo material
          </Button>
        </div>
      )}

      {embedded && (
        <div className="flex justify-end">
          <Button onClick={handleNew} size="xs">
            <Plus className="h-3.5 w-3.5" />
            Novo material
          </Button>
        </div>
      )}

      {/* Filtros — barra horizontal padronizada (estilo Modelos de Contrato) */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30 rounded-md">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar materiais..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        {!embedded && (
          <Select value={programId} onValueChange={setProgramId}>
            <SelectTrigger className="w-48 h-8 text-sm">
              <SelectValue placeholder="Programa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os programas</SelectItem>
              {programs.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-40 h-8 text-sm">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="image">Imagem</SelectItem>
            <SelectItem value="banner">Banner</SelectItem>
            <SelectItem value="video">Vídeo</SelectItem>
            <SelectItem value="document">Documento</SelectItem>
            <SelectItem value="copy">Texto / Copy</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid compacta (v1.3.0): mais colunas = cards ~35% menores */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-40 w-full rounded-lg" />
          ))}
        </div>

      ) : isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Erro ao carregar materiais. Tente recarregar a página.
          </CardContent>
        </Card>
      ) : filteredMaterials.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground mb-4">
              Nenhum material cadastrado{embedded ? ' neste programa' : ' ainda'}.
            </p>
            <Button onClick={handleNew} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Criar primeiro material
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filteredMaterials.map((m) => (
            <Card key={m.id} className="overflow-hidden">
              {/* Miniatura clicável: abre preview interno (lightbox) */}
              <div
                className={`relative aspect-[4/3] w-full bg-muted ${
                  m.file_url ? 'cursor-zoom-in' : ''
                }`}
                onClick={() => {
                  if (!m.file_url) return;
                  setPreviewMedia({
                    url: m.file_url,
                    type: isImage(m) ? 'image' : isVideo(m) ? 'video' : 'document',
                    title: m.title,
                  });
                }}
              >
                {isImage(m) && m.file_url ? (
                  <img
                    src={m.file_url}
                    alt={m.title}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : isVideo(m) && m.file_url ? (
                  <video
                    src={m.file_url}
                    preload="metadata"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center p-3 text-center">
                    <p className="line-clamp-4 text-xs text-muted-foreground">
                      {m.text_content || stripHtml(m.description) || 'Sem preview.'}
                    </p>
                  </div>
                )}

                <Badge
                  variant="secondary"
                  className="absolute left-2 top-2 gap-1 bg-background/90 backdrop-blur"
                >
                  <PreviewIcon m={m} />
                  <span className="text-xs">
                    {TYPE_LABEL[m.material_type] ?? m.material_type}
                  </span>
                </Badge>
                {!m.is_active && (
                  <Badge
                    variant="outline"
                    className="absolute right-2 top-2 bg-background/90 backdrop-blur"
                  >
                    Inativo
                  </Badge>
                )}
              </div>
              <CardContent className="p-2.5 space-y-1.5">
                <div>
                  <h3 className="text-sm font-semibold leading-tight line-clamp-1">
                    {m.title}
                  </h3>
                  {m.description && (
                    /* v1.4.0 — descrição pode conter HTML do editor rico */
                    <div
                      className="line-clamp-2 text-[11px] text-muted-foreground [&_p]:m-0"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(m.description),
                      }}
                    />
                  )}
                </div>
                <div className="flex items-center justify-between gap-1">
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => handleToggleActive(m)}
                    disabled={updateMutation.isPending}
                  >
                    {m.is_active ? 'Desativar' : 'Ativar'}
                  </Button>
                  <div className="flex gap-0.5">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => handleEdit(m)}
                      title="Editar"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => setConfirmDelete(m)}
                      title="Excluir"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>

            </Card>
          ))}
        </div>
      )}

      <ReferralMaterialFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        material={selected}
        defaultProgramId={embeddedProgramId}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir material?</AlertDialogTitle>
            <AlertDialogDescription>
              O arquivo armazenado também será removido. Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview interno da mídia (v1.3.0) */}
      <ImageLightbox
        open={!!previewMedia}
        onClose={() => setPreviewMedia(null)}
        mediaItems={previewMedia ? [previewMedia] : []}
        caption={previewMedia?.title ?? null}
      />
    </div>

  );
}
